import { useNavigate } from 'react-router-dom'
import { useAddBook } from '../hooks/useAddBook'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CatalogSearchStep from '../components/addBook/CatalogSearchStep'
import ManualChaptersForm from '../components/addBook/ManualChaptersForm'
import ErrorMessage from '../components/ErrorMessage'

export default function AddBook() {
  const navigate = useNavigate()
  const ab = useAddBook()
  const {
    mode, setMode, title, setTitle, author, setAuthor, submitting, error, pdfFile, setPdfFile, koboFile, setKoboFile, koboResult, kindleFile, setKindleFile, kindleResult, readwiseFile, setReadwiseFile, readwiseResult, handlePdfImport, handleKoboImport, handleKindleClippingsImport, handleReadwiseImport,
  } = ab

  if (mode === 'search') return <CatalogSearchStep ab={ab} />

  if (mode === 'manual') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/library')} sx={{ mb: 2 }}>
          Back to Library
        </Button>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>Add Book</Typography>

        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); if (title.trim() && author.trim()) setMode('highlights') }}>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Thinking, Fast and Slow"
              required
              fullWidth
            />
            <TextField
              label="Author"
              value={author}
              onChange={e => setAuthor(e.target.value)}
              placeholder="e.g. Daniel Kahneman"
              required
              fullWidth
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" type="submit">Next: Add Highlights</Button>
              <Button variant="text" onClick={() => setMode('search')}>← Back to search</Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    )
  }

  if (mode === 'pdf') {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setMode('search')} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Import from PDF</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload a paper, article, or excerpt (not a whole book). We'll extract the text and generate practice questions from it.
        </Typography>

        <Box component="form" onSubmit={handlePdfImport}>
          <Stack spacing={2}>
            <TextField
              label="Book title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Author"
              value={author}
              onChange={e => setAuthor(e.target.value)}
              required
              fullWidth
            />
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                {pdfFile ? pdfFile.name : 'Choose PDF file'}
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  hidden
                  onChange={e => setPdfFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Box>

            {error && <ErrorMessage message={error} />}

            <Button
              variant="contained"
              type="submit"
              disabled={submitting || !pdfFile || !title.trim() || !author.trim()}
            >
              {submitting ? <CircularProgress size={18} /> : <span>Import PDF</span>}
            </Button>
          </Stack>
        </Box>
      </Box>
    )
  }

  if (mode === 'kobo') {
    if (koboResult) {
      return (
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Import complete</Typography>
          <Typography sx={{ mb: 3 }}>
            Imported <strong>{koboResult.books}</strong> book{koboResult.books !== 1 ? 's' : ''} with{' '}
            <strong>{koboResult.passages}</strong> highlight{koboResult.passages !== 1 ? 's' : ''}.
            Questions are being generated in the background.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/library')}>Go to Library</Button>
        </Box>
      )
    }

    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setMode('search')} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Import Kobo Highlights</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Export your annotations from Kobo: open the Kobo desktop app → My Books → select a book → Export Annotations (.csv).
          A single export can contain multiple books.
        </Typography>

        <Box component="form" onSubmit={handleKoboImport}>
          <Stack spacing={2}>
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                {koboFile ? koboFile.name : 'Choose Kobo CSV file'}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={e => setKoboFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Box>

            {error && <ErrorMessage message={error} />}

            <Button
              variant="contained"
              type="submit"
              disabled={submitting || !koboFile}
            >
              {submitting ? <CircularProgress size={18} /> : <span>Import highlights</span>}
            </Button>
          </Stack>
        </Box>
      </Box>
    )
  }

  if (mode === 'kindle-clippings') {
    if (kindleResult) {
      return (
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Import complete</Typography>
          <Typography sx={{ mb: 3 }}>
            Imported <strong>{kindleResult.books}</strong> book{kindleResult.books !== 1 ? 's' : ''} with{' '}
            <strong>{kindleResult.passages}</strong> highlight{kindleResult.passages !== 1 ? 's' : ''}.
            Questions are being generated in the background.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/library')}>Go to Library</Button>
        </Box>
      )
    }

    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setMode('search')} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Import Kindle Device Highlights</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Connect your Kindle to your computer via USB, then copy the file &lsquo;My Clippings.txt&rsquo; from the
          Kindle&rsquo;s root folder. This file contains all your highlights.
        </Typography>

        <Box component="form" onSubmit={handleKindleClippingsImport}>
          <Stack spacing={2}>
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                {kindleFile ? kindleFile.name : 'Choose My Clippings.txt'}
                <input
                  type="file"
                  accept=".txt,text/plain"
                  hidden
                  onChange={e => setKindleFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Box>

            {error && <ErrorMessage message={error} />}

            <Button
              variant="contained"
              type="submit"
              disabled={submitting || !kindleFile}
            >
              {submitting ? <CircularProgress size={18} /> : <span>Import highlights</span>}
            </Button>
          </Stack>
        </Box>
      </Box>
    )
  }

  if (mode === 'readwise') {
    if (readwiseResult) {
      return (
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>Import complete</Typography>
          <Typography sx={{ mb: 3 }}>
            Imported <strong>{readwiseResult.books}</strong> book{readwiseResult.books !== 1 ? 's' : ''} with{' '}
            <strong>{readwiseResult.passages}</strong> highlight{readwiseResult.passages !== 1 ? 's' : ''}.
            Questions are being generated in the background.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/library')}>Go to Library</Button>
        </Box>
      )
    }

    return (
      <Box sx={{ maxWidth: 600, mx: 'auto' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => setMode('search')} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Import from Readwise</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Export your highlights from Readwise: go to readwise.io/export, choose &lsquo;CSV&rsquo; format, and upload
          the downloaded file. One export can contain highlights from all your books.
        </Typography>

        <Box component="form" onSubmit={handleReadwiseImport}>
          <Stack spacing={2}>
            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                {readwiseFile ? readwiseFile.name : 'Choose Readwise CSV'}
                <input
                  type="file"
                  accept=".csv,text/csv"
                  hidden
                  onChange={e => setReadwiseFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Box>

            {error && <ErrorMessage message={error} />}

            <Button
              variant="contained"
              type="submit"
              disabled={submitting || !readwiseFile}
            >
              {submitting ? <CircularProgress size={18} /> : <span>Import highlights</span>}
            </Button>
          </Stack>
        </Box>
      </Box>
    )
  }

  return <ManualChaptersForm ab={ab} />
}
